import { get, has, times, includes } from 'lodash';

export const isVehicleMileage = apiKey => /^input_footprint_transportation_miles/.test(apiKey);

export const isVehicleEfficiency = apiKey => /^input_footprint_transportation_mpg/.test(apiKey);

export const getVehicleIndex = apiKey =>
  apiKey.match(/^input_footprint_transportation_(mpg|miles|fuel)(\d{1,2})$/)[2];

export const isElectricVehicle = (apiKey, state) => get(state.user_footprint, `electricVehicles.${getVehicleIndex(apiKey)}`, false);

export const isCleanEnergyPercent = apiKey => includes([
  'input_takeaction_purchase_green_electricity_percentclean',
  'input_footprint_housing_cleanpercent',
], apiKey);

export const tradeInVehicleTypes = {
  ELECTRIC: 'electric',
  HYBRID: 'hybrid',
  ALTERNATIVE_FUEL: 'alternativefuel',
  MORE_EFFICIENT: 'more_efficient',
};

export const inputTakeactionKey = apiKey => apiKey.replace(/^input_takeaction_/, '');

export const isElectricVehicleMileage = (apiKey, state) =>
  isVehicleMileage(apiKey) && isElectricVehicle(apiKey, state);

const isCurrencyAmount = apiKey => includes([
  'input_takeaction_assumption_price_of_gasoline',
  'input_takeaction_low_flow_showerheads_nat_gas_gallon',
  'input_takeaction_install_PV_panels_w_cost',
  'input_takeaction_purchase_green_electricity_costclean',
  'input_takeaction_telecommute_to_work_parking',
  'input_takeaction_water_efficient_landscaping_cost_drought',
  'input_takeaction_water_efficient_landscaping_cost_drip',
  'input_takeaction_T12toT8_T8cost',
  'input_takeaction_switch_to_cfl_cflcost',
  'input_takeaction_switch_to_cfl_incancost',
  'input_takeaction_rechargeable_batteries_cost_aaa',
  'input_takeaction_rechargeable_batteries_cost_a',
  'input_takeaction_rechargeable_batteries_cost_c',
  'input_takeaction_rechargeable_batteries_cost_d',
  'input_takeaction_rechargeable_batteries_cost_9v',
  'input_takeaction_assumption_ngprice',
], apiKey);

const precisions = {
  input_takeaction_low_flow_faucets_conventional: 1,
  input_takeaction_low_flow_faucets_efficient: 1,
  input_takeaction_energy_star_fridge_fresh: 2,
  input_takeaction_energy_star_fridge_freezer: 2,
  result_takeaction_energy_star_fridge_totvolume: 2,
  input_takeaction_assumption_ngprice: 2,
  input_takeaction_low_flow_faucets_minutes: 1,
  input_takeaction_assumption_kwhprice: 1,
};

export const getPrecision = (apiKey) => {
  if (isVehicleEfficiency(apiKey)) return 1;
  if (isCurrencyAmount(apiKey)) return 2;
  if (has(precisions, apiKey)) return precisions[apiKey];
  return 0;
};

export const getSuffix = apiKey => apiKey.replace(/^(input|result)_(footprint|takeaction)?_?/, '');

export const isResult = apiKey => /^result_/.test(apiKey);
export const isTakeActionInput = apiKey => /^input_takeaction_/.test(apiKey);

const unitsMap = {
  input_footprint_housing_heatingoil_type: {
    0: 'input_footprint_housing_heatingoil_dollars',
    1: 'input_footprint_housing_heatingoil_gallons',
  },
  input_footprint_housing_naturalgas_type: {
    0: 'input_footprint_housing_naturalgas_dollars',
    1: 'input_footprint_housing_naturalgas_therms',
    2: 'input_footprint_housing_naturalgas_cuft',
  },
  input_footprint_housing_electricity_type: {
    0: 'input_footprint_housing_electricity_dollars',
    1: 'input_footprint_housing_electricity_kwh',
  },
};

export const getApiKeyForUnits = (unitApiKey, value) => unitsMap[unitApiKey][value];

export const isTakeActionResult = apiKey => /^result_takeaction_/.test(apiKey);

export const conversions = {
  milesKm: [
    'input_footprint_transportation_publictrans',
    'input_footprint_transportation_bus',
    'input_footprint_transportation_transit',
    'input_footprint_transportation_commuter',
    'input_footprint_transportation_intercity',
    'input_footprint_transportation_airtype',
    'input_footprint_transportation_airtotal',
    'input_footprint_transportation_airshort',
    'input_footprint_transportation_airmedium',
    'input_footprint_transportation_airlong',
    'input_footprint_transportation_airextended',
    'input_takeaction_electric_vehicle_miles_new',
    'input_takeaction_electric_vehicle_miles_old',
    'input_takeaction_hybrid_vehicle_miles_new',
    'input_takeaction_hybrid_vehicle_miles_old',
    'input_takeaction_more_efficient_vehicle_miles_new',
    'input_takeaction_more_efficient_vehicle_miles_old',
    'input_takeaction_alternativefuel_vehicle_miles_new',
    'input_takeaction_alternativefuel_vehicle_miles_old',
    'input_takeaction_ride_my_bike_miles',
    'input_takeaction_take_public_transportation_miles',
    'input_takeaction_telecommute_to_work_miles',
    // result keys
    'result_takeaction_practice_eco_driving_dispmiles',
    'input_takeaction_practice_eco_driving_topspeed',
    'input_takeaction_practice_eco_driving_lowerspeed',
    'input_takeaction_carpool_to_work_dist',
    'result_takeaction_maintain_my_vehicles_dispmiles',
    'input_takeaction_ride_my_bike_miles',
    'display_takeaction_ride_my_bike_miles',
    'input_takeaction_take_public_transportation_gco2bus',
    'input_takeaction_take_public_transportation_gco2ngbus',
    'input_takeaction_take_public_transportation_gco2transit',
    'input_takeaction_take_public_transportation_gco2amtrak',
    'result_takeaction_reduce_air_travel_totalmiles',
    'result_takeaction_reduce_air_travel_miles_alt',
  ].concat(
    times(10).map(n => `input_footprint_transportation_miles${n + 1}`),
  ),
  galLt: [
    'input_footprint_housing_heatingoil_gallons',
    'input_footprint_housing_heatingoil_dollars_per_gallon',
    'input_footprint_housing_heatingoil_gallons',
    'input_takeaction_assumption_price_of_gasoline',
    'input_takeaction_low_flow_showerheads_nat_gas_gallon',
    'input_takeaction_thermostat_winter_fueloilprice',
    'result_takeaction_practice_eco_driving_galsaved',
    'input_takeaction_low_flow_showerheads_old_flow',
    'input_takeaction_low_flow_showerheads_new_flow',
    'input_takeaction_low_flow_toilet_old',
    'input_takeaction_low_flow_toilet_new',
    'input_takeaction_low_flow_faucets_conventional',
    'input_takeaction_low_flow_faucets_efficient',
    'input_takeaction_assumption_price_of_gasoline',
  ],
  sqFtSqM: [
    'input_footprint_housing_squarefeet',
    'input_takeaction_water_efficient_landscaping_cost_drip',
    'input_takeaction_water_efficient_landscaping_cost_drought',
    'display_takeaction_water_efficient_landscaping_sqft',
    'input_takeaction_water_efficient_landscaping_cost_drip',
  ],
  cuftCum: [
    'input_footprint_housing_naturalgas_cuft',
    'input_takeaction_install_solar_heating_ng_consumption',
    'input_takeaction_water_efficient_landscaping_sqft',
    'input_takeaction_energy_star_fridge_fresh',
    'input_takeaction_energy_star_fridge_freezer',
    'result_takeaction_energy_star_fridge_totvolume',
    'result_takeaction_energy_star_fridge_fridgekwh',
    'input_takeaction_install_solar_heating_ng_consumption',
    'input_takeaction_install_solar_heating_cost',
  ],
  ngThermsCum: [
    'input_takeaction_assumption_ngprice',
  ],
  cuyCum: [
    'input_takeaction_reduce_comm_waste_per_week',
  ],
  farenheitCelsius: [
    'input_takeaction_tankless_water_heater_temp',
  ],
  farenheitCelsiusDiff: [
    'input_takeaction_thermostat_summer_daydegrees',
    'input_takeaction_thermostat_summer_nightdegrees',
    'input_takeaction_thermostat_winter_daydegrees',
    'input_takeaction_thermostat_winter_nightdegrees',
    'input_takeaction_thermostat_winter_hdd',
    'input_takeaction_thermostat_summer_cdd',
    'input_takeaction_purchase_high_efficiency_cooling_cdd',
  ],
  mpgLtPer100Km: [
    'input_takeaction_electric_vehicle_mpg_new',
    'input_takeaction_electric_vehicle_mpg_old',
    'input_takeaction_hybrid_vehicle_mpg_new',
    'input_takeaction_hybrid_vehicle_mpg_old',
    'input_takeaction_more_efficient_vehicle_mpg_new',
    'input_takeaction_more_efficient_vehicle_mpg_old',
    'input_takeaction_alternativefuel_vehicle_mpg_new',
    'input_takeaction_alternativefuel_vehicle_mpg_old',
    'input_takeaction_ride_my_bike_mpg',
    'input_takeaction_take_public_transportation_mpg',
    'input_takeaction_telecommute_to_work_mpg',
    'result_takeaction_practice_eco_driving_mpg',
    'result_takeaction_practice_eco_driving_newmpg',
    'input_takeaction_carpool_to_work_mpgvehu',
    'input_takeaction_carpool_to_work_mpgvehc',
    'result_takeaction_maintain_my_vehicles_mpg',
  ],
  inverse: {
    milesKm: [
      'input_takeaction_take_public_transportation_gco2bus',
      'input_takeaction_take_public_transportation_gco2ngbus',
      'input_takeaction_take_public_transportation_gco2transit',
      'input_takeaction_take_public_transportation_gco2amtrak',
    ],
    galLt: [
      'input_footprint_housing_heatingoil_dollars_per_gallon',
      'input_takeaction_assumption_price_of_gasoline',
      'input_takeaction_low_flow_showerheads_nat_gas_gallon',
      'input_takeaction_thermostat_winter_fueloilprice',
      'input_takeaction_assumption_price_of_gasoline',
    ],
    sqFtSqM: [
      'input_takeaction_water_efficient_landscaping_cost_drip',
      'input_takeaction_water_efficient_landscaping_cost_drought',
    ],
    cuftCum: [
      'result_takeaction_energy_star_fridge_fridgekwh',
      'input_takeaction_install_solar_heating_cost',
    ],
    ngThermsCum: [
      'input_takeaction_assumption_ngprice',
    ],
  },
};

export const takeactionInputs = [
  'input_takeaction_T12toT8',
  'input_takeaction_T12toT8_T12watt',
  'input_takeaction_T12toT8_T8',
  'input_takeaction_T12toT8_T8cost',
  'input_takeaction_T12toT8_T8watt',
  'input_takeaction_T12toT8_hours',
  'input_takeaction_alternativefuel_vehicle',
  'input_takeaction_alternativefuel_vehicle_miles_new',
  'input_takeaction_alternativefuel_vehicle_miles_old',
  'input_takeaction_alternativefuel_vehicle_mpg_new',
  'input_takeaction_alternativefuel_vehicle_mpg_old',
  'input_takeaction_alternativefuel_vehicle_new_fuel_cost',
  'input_takeaction_alternativefuel_vehicle_purchase',
  'input_takeaction_alternativefuel_vehicle_rebates',
  'input_takeaction_alternativefuel_vehicle_sell',
  'input_takeaction_assumption_discountrate',
  'input_takeaction_assumption_inflationrate',
  'input_takeaction_assumption_kwhprice',
  'input_takeaction_assumption_ngprice',
  'input_takeaction_assumption_price_of_gasoline',
  'input_takeaction_carpool_to_work',
  'input_takeaction_carpool_to_work_category',
  'input_takeaction_carpool_to_work_days',
  'input_takeaction_carpool_to_work_daysper',
  'input_takeaction_carpool_to_work_depvehu',
  'input_takeaction_carpool_to_work_dist',
  'input_takeaction_carpool_to_work_fees',
  'input_takeaction_carpool_to_work_mpgvehc',
  'input_takeaction_carpool_to_work_mpgvehu',
  'input_takeaction_carpool_to_work_passenger',
  'input_takeaction_electric_vehicle',
  'input_takeaction_electric_vehicle_miles_new',
  'input_takeaction_electric_vehicle_miles_old',
  'input_takeaction_electric_vehicle_mpg_new',
  'input_takeaction_electric_vehicle_mpg_old',
  'input_takeaction_electric_vehicle_purchase',
  'input_takeaction_electric_vehicle_rebates',
  'input_takeaction_electric_vehicle_sell',
  'input_takeaction_energy_star_copiers',
  'input_takeaction_energy_star_copiers_amount',
  'input_takeaction_energy_star_copiers_annual_savings',
  'input_takeaction_energy_star_copiers_cost',
  'input_takeaction_energy_star_desktops',
  'input_takeaction_energy_star_desktops_amount',
  'input_takeaction_energy_star_desktops_annual_savings',
  'input_takeaction_energy_star_desktops_cost',
  'input_takeaction_energy_star_fridge',
  'input_takeaction_energy_star_fridge_freezer',
  'input_takeaction_energy_star_fridge_fresh',
  'input_takeaction_energy_star_fridge_fridgecost',
  'input_takeaction_energy_star_fridge_fridgetype',
  'input_takeaction_energy_star_printers',
  'input_takeaction_energy_star_printers_amount',
  'input_takeaction_energy_star_printers_annual_savings',
  'input_takeaction_energy_star_printers_cost',
  'input_takeaction_go_organic',
  'input_takeaction_go_organic_eatorgdairy',
  'input_takeaction_go_organic_eatorgmeat',
  'input_takeaction_go_organic_eatorgproduce',
  'input_takeaction_go_organic_mydairy',
  'input_takeaction_go_organic_mydairycost',
  'input_takeaction_go_organic_mymeat',
  'input_takeaction_go_organic_mymeatcost',
  'input_takeaction_go_organic_myprodcost',
  'input_takeaction_go_organic_myproduce',
  'input_takeaction_go_organic_pledgedairy',
  'input_takeaction_go_organic_pledgeorgmeat',
  'input_takeaction_go_organic_pledgeorgprod',
  'input_takeaction_hybrid_vehicle',
  'input_takeaction_hybrid_vehicle_miles_new',
  'input_takeaction_hybrid_vehicle_miles_old',
  'input_takeaction_hybrid_vehicle_mpg_new',
  'input_takeaction_hybrid_vehicle_mpg_old',
  'input_takeaction_hybrid_vehicle_purchase',
  'input_takeaction_hybrid_vehicle_rebates',
  'input_takeaction_hybrid_vehicle_sell',
  'input_takeaction_install_PV_panels',
  'input_takeaction_install_PV_panels_percent',
  'input_takeaction_install_PV_panels_w_cost',
  'input_takeaction_install_solar_heating',
  'input_takeaction_install_solar_heating_cost',
  'input_takeaction_install_solar_heating_ng_consumption',
  'input_takeaction_install_solar_heating_percent',
  'input_takeaction_line_dry_clothing',
  'input_takeaction_line_dry_clothing_dryerldwk',
  'input_takeaction_line_dry_clothing_percentdry',
  'input_takeaction_low_carbon_diet',
  'input_takeaction_low_carbon_diet_cerealsfootprint',
  'input_takeaction_low_carbon_diet_dairyfootprint',
  'input_takeaction_low_carbon_diet_fvfootprint',
  'input_takeaction_low_carbon_diet_meatfootprint',
  'input_takeaction_low_carbon_diet_otherfootprint',
  'input_takeaction_low_flow_faucets',
  'input_takeaction_low_flow_faucets_amount',
  'input_takeaction_low_flow_faucets_avg_daily_use',
  'input_takeaction_low_flow_faucets_conventional',
  'input_takeaction_low_flow_faucets_cost',
  'input_takeaction_low_flow_faucets_efficient',
  'input_takeaction_low_flow_faucets_minutes',
  'input_takeaction_low_flow_faucets_replaced',
  'input_takeaction_low_flow_showerheads',
  'input_takeaction_low_flow_showerheads_amount',
  'input_takeaction_low_flow_showerheads_amount_replaced',
  'input_takeaction_low_flow_showerheads_avg_showers',
  'input_takeaction_low_flow_showerheads_cost',
  'input_takeaction_low_flow_showerheads_length',
  'input_takeaction_low_flow_showerheads_nat_gas_gallon',
  'input_takeaction_low_flow_showerheads_new_flow',
  'input_takeaction_low_flow_showerheads_old_flow',
  'input_takeaction_low_flow_toilet',
  'input_takeaction_low_flow_toilet_amount',
  'input_takeaction_low_flow_toilet_cost',
  'input_takeaction_low_flow_toilet_new',
  'input_takeaction_low_flow_toilet_old',
  'input_takeaction_maintain_my_vehicles',
  'input_takeaction_maintain_my_vehicles_airfilter',
  'input_takeaction_maintain_my_vehicles_filtercost',
  'input_takeaction_maintain_my_vehicles_filterpercent',
  'input_takeaction_maintain_my_vehicles_tires',
  'input_takeaction_maintain_my_vehicles_tirescost',
  'input_takeaction_maintain_my_vehicles_tirespercent',
  'input_takeaction_more_efficient_vehicle',
  'input_takeaction_more_efficient_vehicle_miles_new',
  'input_takeaction_more_efficient_vehicle_miles_old',
  'input_takeaction_more_efficient_vehicle_mpg_new',
  'input_takeaction_more_efficient_vehicle_mpg_old',
  'input_takeaction_more_efficient_vehicle_purchase',
  'input_takeaction_more_efficient_vehicle_sell',
  'input_takeaction_offset_housing',
  'input_takeaction_offset_housing_offset',
  'input_takeaction_offset_shopping',
  'input_takeaction_offset_shopping_offset',
  'input_takeaction_offset_transportation',
  'input_takeaction_offset_transportation_offset',
  'input_takeaction_plant_trees',
  'input_takeaction_plant_trees_amount',
  'input_takeaction_plant_trees_cost',
  'input_takeaction_plant_trees_offset',
  'input_takeaction_power_mgmt_comp',
  'input_takeaction_power_mgmt_comp_desktops',
  'input_takeaction_power_mgmt_comp_monitors',
  'input_takeaction_power_mgmt_comp_percent_d',
  'input_takeaction_power_mgmt_comp_percent_m',
  'input_takeaction_practice_eco_driving',
  'input_takeaction_practice_eco_driving_hwpercent',
  'input_takeaction_practice_eco_driving_lowerspeed',
  'input_takeaction_practice_eco_driving_topspeed',
  'input_takeaction_practice_eco_driving_topspercent',
  'input_takeaction_print_double_sided',
  'input_takeaction_print_double_sided_number',
  'input_takeaction_purchase_green_electricity',
  'input_takeaction_purchase_green_electricity_costclean',
  'input_takeaction_purchase_green_electricity_kwhcost',
  'input_takeaction_purchase_green_electricity_percentclean',
  'input_takeaction_purchase_high_efficiency_cooling',
  'input_takeaction_purchase_high_efficiency_cooling_cdd',
  'input_takeaction_purchase_high_efficiency_cooling_conventional_rating',
  'input_takeaction_purchase_high_efficiency_cooling_cost_difference',
  'input_takeaction_purchase_high_efficiency_cooling_energystar_rating',
  'input_takeaction_purchase_high_efficiency_cooling_percentuse',
  'input_takeaction_purchase_high_efficiency_heating',
  'input_takeaction_purchase_high_efficiency_heating_conventional_eff',
  'input_takeaction_purchase_high_efficiency_heating_cost_difference',
  'input_takeaction_purchase_high_efficiency_heating_energystar_eff',
  'input_takeaction_purchase_high_efficiency_heating_fueloilprice',
  'input_takeaction_purchase_high_efficiency_heating_fueltype',
  'input_takeaction_purchase_high_efficiency_heating_hdd',
  'input_takeaction_purchase_high_efficiency_heating_percentuse',
  'input_takeaction_rechargeable_batteries',
  'input_takeaction_rechargeable_batteries_amount_9v',
  'input_takeaction_rechargeable_batteries_amount_aa',
  'input_takeaction_rechargeable_batteries_amount_aaa',
  'input_takeaction_rechargeable_batteries_amount_c',
  'input_takeaction_rechargeable_batteries_amount_d',
  'input_takeaction_rechargeable_batteries_carbon_emissions',
  'input_takeaction_rechargeable_batteries_charge_energy',
  'input_takeaction_rechargeable_batteries_cost_9v',
  'input_takeaction_rechargeable_batteries_cost_aa',
  'input_takeaction_rechargeable_batteries_cost_aaa',
  'input_takeaction_rechargeable_batteries_cost_c',
  'input_takeaction_rechargeable_batteries_cost_d',
  'input_takeaction_rechargeable_batteries_cost_multiple',
  'input_takeaction_rechargeable_batteries_uses',
  'input_takeaction_rechargeable_batteries_weight_9v',
  'input_takeaction_rechargeable_batteries_weight_aa',
  'input_takeaction_rechargeable_batteries_weight_aaa',
  'input_takeaction_rechargeable_batteries_weight_c',
  'input_takeaction_rechargeable_batteries_weight_d',
  'input_takeaction_reduce_air_travel',
  'input_takeaction_reduce_air_travel_miles_percent',
  'input_takeaction_reduce_comm_waste',
  'input_takeaction_reduce_comm_waste_per_week',
  'input_takeaction_reduce_comm_waste_percent',
  'input_takeaction_ride_my_bike',
  'input_takeaction_ride_my_bike_include_foodemissions',
  'input_takeaction_ride_my_bike_miles',
  'input_takeaction_ride_my_bike_mpg',
  'input_takeaction_switch_to_cfl',
  'input_takeaction_switch_to_cfl_cfl',
  'input_takeaction_switch_to_cfl_cflcost',
  'input_takeaction_switch_to_cfl_cfllife',
  'input_takeaction_switch_to_cfl_cflwatt',
  'input_takeaction_switch_to_cfl_hours',
  'input_takeaction_switch_to_cfl_incancost',
  'input_takeaction_switch_to_cfl_incanlife',
  'input_takeaction_switch_to_cfl_incanwatt',
  'input_takeaction_take_public_transportation',
  'input_takeaction_take_public_transportation_gco2amtrak',
  'input_takeaction_take_public_transportation_gco2bus',
  'input_takeaction_take_public_transportation_gco2ngbus',
  'input_takeaction_take_public_transportation_gco2transit',
  'input_takeaction_take_public_transportation_miles',
  'input_takeaction_take_public_transportation_mpg',
  'input_takeaction_take_public_transportation_parking',
  'input_takeaction_take_public_transportation_ptfare',
  'input_takeaction_take_public_transportation_type',
  'input_takeaction_tankless_water_heater',
  'input_takeaction_tankless_water_heater_fueltype',
  'input_takeaction_tankless_water_heater_replacement_cost',
  'input_takeaction_tankless_water_heater_replacements',
  'input_takeaction_tankless_water_heater_temp',
  'input_takeaction_telecommute_to_work',
  'input_takeaction_telecommute_to_work_days',
  'input_takeaction_telecommute_to_work_miles',
  'input_takeaction_telecommute_to_work_mpg',
  'input_takeaction_telecommute_to_work_parking',
  'input_takeaction_telecommute_to_work_times',
  'input_takeaction_telecommute_to_work_worth',
  'input_takeaction_thermostat_summer',
  'input_takeaction_thermostat_summer_cdd',
  'input_takeaction_thermostat_summer_daydegrees',
  'input_takeaction_thermostat_summer_nightdegrees',
  'input_takeaction_thermostat_summer_percentuse',
  'input_takeaction_thermostat_summer_wdntsetup',
  'input_takeaction_thermostat_summer_wdsetup',
  'input_takeaction_thermostat_summer_wentsetup',
  'input_takeaction_thermostat_summer_wesetup',
  'input_takeaction_thermostat_winter',
  'input_takeaction_thermostat_winter_daydegrees',
  'input_takeaction_thermostat_winter_fueloilprice',
  'input_takeaction_thermostat_winter_fueltype',
  'input_takeaction_thermostat_winter_hdd',
  'input_takeaction_thermostat_winter_nightdegrees',
  'input_takeaction_thermostat_winter_percentuse',
  'input_takeaction_thermostat_winter_wdntsetup',
  'input_takeaction_thermostat_winter_wdsetup',
  'input_takeaction_thermostat_winter_wentsetup',
  'input_takeaction_thermostat_winter_wesetup',
  'input_takeaction_turn_off_lights',
  'input_takeaction_turn_off_lights_hours',
  'input_takeaction_turn_off_lights_number',
  'input_takeaction_turn_off_lights_watts',
  'input_takeaction_water_efficient_landscaping',
  'input_takeaction_water_efficient_landscaping_cost_drip',
  'input_takeaction_water_efficient_landscaping_cost_drought',
  'input_takeaction_water_efficient_landscaping_drought_new',
  'input_takeaction_water_efficient_landscaping_drought_old',
  'input_takeaction_water_efficient_landscaping_grass_drip_new',
  'input_takeaction_water_efficient_landscaping_grass_drip_old',
  'input_takeaction_water_efficient_landscaping_grass_new',
  'input_takeaction_water_efficient_landscaping_grass_old',
  'input_takeaction_water_efficient_landscaping_plant_drip_new',
  'input_takeaction_water_efficient_landscaping_plant_drip_old',
  'input_takeaction_water_efficient_landscaping_plants_new',
  'input_takeaction_water_efficient_landscaping_plants_old',
  'input_takeaction_water_efficient_landscaping_sqft',
];

export const inputKeys = [
  'input_footprint_household_adults',
  'input_footprint_household_children',
  'input_footprint_transportation_num_vehicles',

  'input_footprint_transportation_groundtype',
  'input_footprint_transportation_publictrans',
  'input_footprint_transportation_bus',
  'input_footprint_transportation_transit',
  'input_footprint_transportation_commuter',
  'input_footprint_transportation_intercity',
  'input_footprint_transportation_airtype',
  'input_footprint_transportation_airtotal',
  'input_footprint_transportation_airshort',
  'input_footprint_transportation_airmedium',
  'input_footprint_transportation_airlong',
  'input_footprint_transportation_airextended',
  'input_footprint_housing_cdd',
  'input_footprint_housing_hdd',
  'input_footprint_housing_electricity_type',
  'input_footprint_housing_electricity_dollars',
  'input_footprint_housing_electricity_kwh',
  'input_footprint_housing_cleanpercent',
  'input_footprint_housing_naturalgas_type',
  'input_footprint_housing_naturalgas_dollars',
  'input_footprint_housing_naturalgas_therms',
  'input_footprint_housing_naturalgas_cuft',
  'input_footprint_housing_heatingoil_type',
  'input_footprint_housing_heatingoil_dollars',
  'input_footprint_housing_heatingoil_gallons',
  'input_footprint_housing_heatingoil_dollars_per_gallon',
  'input_footprint_housing_squarefeet',
  'input_footprint_housing_watersewage',
  'input_footprint_housing_gco2_per_kwh',

  'input_footprint_shopping_food_meatfisheggs_default',
  'input_footprint_shopping_food_meat_beefpork_default',
  'input_footprint_shopping_food_meat_poultry_default',
  'input_footprint_shopping_food_meat_fish_default',
  'input_footprint_shopping_food_meat_other_default',
  'input_footprint_shopping_food_dairy_default',
  'input_footprint_shopping_food_fruitvegetables_default',
  'input_footprint_shopping_food_cereals_default',
  'input_footprint_shopping_food_otherfood_default',
  'input_footprint_shopping_food_meattype',
  'input_footprint_shopping_food_meatfisheggs',
  'input_footprint_shopping_food_meat_beefpork',
  'input_footprint_shopping_food_meat_poultry',
  'input_footprint_shopping_food_meat_fish',
  'input_footprint_shopping_food_meat_other',
  'input_footprint_shopping_food_dairy',
  'input_footprint_shopping_food_fruitvegetables',
  'input_footprint_shopping_food_cereals',
  'input_footprint_shopping_food_otherfood',

  'input_footprint_shopping_goods_default_furnitureappliances',
  'input_footprint_shopping_goods_default_clothing',
  'input_footprint_shopping_goods_default_other_entertainment',
  'input_footprint_shopping_goods_default_other_office',
  'input_footprint_shopping_goods_default_other_personalcare',
  'input_footprint_shopping_goods_default_other_autoparts',
  'input_footprint_shopping_goods_default_other_medical',

  'input_footprint_shopping_goods_type',
  'input_footprint_shopping_goods_total',
  'input_footprint_shopping_goods_furnitureappliances',
  'input_footprint_shopping_goods_clothing',
  'input_footprint_shopping_goods_other_type',
  'input_footprint_shopping_goods_other_total',
  'input_footprint_shopping_goods_other_entertainment',
  'input_footprint_shopping_goods_other_office',
  'input_footprint_shopping_goods_other_personalcare',
  'input_footprint_shopping_goods_other_autoparts',
  'input_footprint_shopping_goods_other_medical',
  'input_footprint_shopping_services_type',
  'input_footprint_shopping_services_total',
  'input_footprint_shopping_services_healthcare',
  'input_footprint_shopping_services_education',
  'input_footprint_shopping_services_communications',
  'input_footprint_shopping_services_vehicleservices',
  'input_footprint_shopping_services_finance',
  'input_footprint_shopping_services_household',
  'input_footprint_shopping_services_charity',
  'input_footprint_shopping_services_miscservices',
].concat(
  times(10).map(n => `input_footprint_transportation_miles${n + 1}`),
  times(10).map(n => `input_footprint_transportation_mpg${n + 1}`),
  times(10).map(n => `input_footprint_transportation_fuel${n + 1}`),
  takeactionInputs,
);
